import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useEliminarPrecioDeIa } from "@/hooks/useConsumoDeIa";
import { getApiErrorMessage } from "@/lib/api";
import type { IaPrecio } from "@/lib/consumoDeIa";

interface EliminarPrecioDeIaDialogProps {
  /** El precio a retirar; `null` cierra el diálogo. */
  precio: IaPrecio | null;
  onCerrar: () => void;
}

/**
 * Confirmación de borrado de un período de precio.
 *
 * NO AFECTA NINGÚN CONSUMO YA REGISTRADO: el costo de cada petición pasada ya
 * quedó calculado y congelado en su propia fila, sin ninguna FK que dependa de
 * este catálogo (`IaPrecioController`, docblock de la clase). El aviso lo dice
 * con todas las letras para que borrar un precio viejo no se sienta como
 * borrar el gasto histórico.
 */
export function EliminarPrecioDeIaDialog({ precio, onCerrar }: EliminarPrecioDeIaDialogProps) {
  const [error, setError] = useState<string | null>(null);

  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const eliminar = useEliminarPrecioDeIa();

  if (!precio) return null;

  const enVuelo = eliminar.isPending && !eliminar.isPaused;

  return (
    <AlertDialog
      open
      onOpenChange={(abierto) => {
        if (!abierto && !enVuelo) onCerrar();
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(evento) => {
          if (enVuelo) evento.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            ¿Eliminar el precio de «{precio.modelo}» vigente desde {precio.vigente_desde}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            No cambia ningún costo ya registrado: cada petición pasada guarda el suyo, congelado
            al momento en que ocurrió.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error ? (
          <div
            role="alert"
            className="bg-danger-soft text-danger-soft-foreground rounded-md px-3.5 py-3 text-sm"
          >
            <p className="font-bold">{error}</p>
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={enVuelo}>Cancelar</AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={enVuelo}
            className="gap-2"
            onClick={() => {
              setError(null);
              eliminar.mutate(precio.id, {
                onSuccess: () => onCerrar(),
                onError: (fallo) => {
                  const mensaje = getApiErrorMessage(
                    fallo,
                    "No pudimos eliminar el precio. Inténtalo de nuevo.",
                  );
                  if (montado.current) {
                    setError(mensaje);
                    return;
                  }
                  toast.error(mensaje);
                },
              });
            }}
          >
            {enVuelo ? <Spinner /> : null}
            Eliminar precio
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
